'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Modal } from '@shared/ui/modal/Modal';
import { PrimaryButton } from '@shared/ui/button/PrimaryButton';
import { PasswordInput } from '@shared/ui/inputs/PasswordInput';
import { Typography } from '@shared/ui/typography/Typography';
import { Icon } from '@shared/ui/icons/Icon';
import type { BaseModalProps } from '@shared/types/modal.types';
import { useChangePassword } from '@entities/user/model/hooks';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { useRouter } from 'next/navigation';

interface ChangePasswordModalProps extends BaseModalProps {
  onSuccess?: () => void;
}

/**
 * ChangePasswordModal - Modal for changing user password
 * Requires current password verification
 * Invalidates all sessions after successful change
 */
export function ChangePasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ChangePasswordModalProps) {
  const router = useRouter();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const t = useTranslations('profile.privacy');
  const changePasswordMutation = useChangePassword();
  const { logout } = useAuthStore();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Current password validation
    if (!formData.currentPassword) {
      newErrors.currentPassword = t('changePassword.errors.currentPasswordRequired');
    }

    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = t('changePassword.errors.newPasswordRequired');
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('changePassword.errors.passwordTooShort');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(formData.newPassword)) {
      newErrors.newPassword = t('changePassword.errors.passwordRequirements');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.passwordsDoNotMatch');
    }

    // Check if new password is same as current
    if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = t('changePassword.errors.samePassword');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      // Show success message
      setSuccessMessage(t('changePassword.success'));

      // Clear form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Wait 2 seconds to show success message, then logout and redirect
      setTimeout(() => {
        logout();
        onClose();
        router.push('/login');
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);

    } catch (error: any) {
      // Handle specific errors
      if (error?.message?.includes('Current password is incorrect')) {
        setErrors({ currentPassword: t('changePassword.errors.incorrectPassword') });
      } else if (error?.message?.includes('New password must be different')) {
        setErrors({ newPassword: t('changePassword.errors.samePassword') });
      } else if (error?.message?.includes('OAuth-only')) {
        setErrors({ submit: t('changePassword.errors.oauthAccount') });
      } else {
        setErrors({ submit: error?.message || t('changePassword.errors.generic') });
      }
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    setSuccessMessage('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-primary/10 rounded-full">
              <Icon name="password" size="md" color="accent" />
            </div>
            <Typography variant="h2" weight="bold">
              {t('changePassword.title')}
            </Typography>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-background-secondary rounded-full transition-colors"
            disabled={changePasswordMutation.isPending}
          >
            <Icon name="x" size="sm" />
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-feedback-success/10 border border-feedback-success rounded-lg flex items-start gap-3">
            <Icon name="check" size="sm" color="success" />
            <div className="flex-1">
              <Typography variant="body" weight="semibold" className="text-feedback-success mb-1">
                {successMessage}
              </Typography>
              <Typography variant="bodySm" color="secondary">
                {t('changePassword.redirecting')}
              </Typography>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block mb-2">
              <Typography variant="body" weight="semibold">
                {t('changePassword.currentPassword')}
              </Typography>
            </label>
            <PasswordInput
              value={formData.currentPassword}
              onChange={(e) => {
                setFormData({ ...formData, currentPassword: e.target.value });
                setErrors({ ...errors, currentPassword: '' });
              }}
              placeholder={t('changePassword.currentPasswordPlaceholder')}
              error={errors.currentPassword}
              disabled={changePasswordMutation.isPending || !!successMessage}
            />
          </div>

          {/* New Password */}
          <div>
            <label className="block mb-2">
              <Typography variant="body" weight="semibold">
                {t('changePassword.newPassword')}
              </Typography>
            </label>
            <PasswordInput
              value={formData.newPassword}
              onChange={(e) => {
                setFormData({ ...formData, newPassword: e.target.value });
                setErrors({ ...errors, newPassword: '' });
              }}
              placeholder={t('changePassword.newPasswordPlaceholder')}
              error={errors.newPassword}
              disabled={changePasswordMutation.isPending || !!successMessage}
            />
            <Typography variant="bodySm" color="secondary" className="mt-1">
              {t('changePassword.passwordHint')}
            </Typography>
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block mb-2">
              <Typography variant="body" weight="semibold">
                {t('changePassword.confirmPassword')}
              </Typography>
            </label>
            <PasswordInput
              value={formData.confirmPassword}
              onChange={(e) => {
                setFormData({ ...formData, confirmPassword: e.target.value });
                setErrors({ ...errors, confirmPassword: '' });
              }}
              placeholder={t('changePassword.confirmPasswordPlaceholder')}
              error={errors.confirmPassword}
              disabled={changePasswordMutation.isPending || !!successMessage}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 bg-feedback-error/10 border border-feedback-error rounded-lg">
              <Typography variant="bodySm" className="text-feedback-error">
                {errors.submit}
              </Typography>
            </div>
          )}

          {/* Security Notice */}
          <div className="p-4 bg-feedback-warning/10 border border-feedback-warning rounded-lg">
            <div className="flex gap-2">
              <Icon name="bell" size="sm" className="text-feedback-warning flex-shrink-0 mt-0.5" />
              <Typography variant="bodySm" color="secondary">
                {t('changePassword.securityNotice')}
              </Typography>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-border-primary rounded-lg hover:bg-background-secondary transition-colors"
              disabled={changePasswordMutation.isPending || !!successMessage}
            >
              <Typography variant="body" weight="semibold">
                {t('changePassword.cancel')}
              </Typography>
            </button>
            <PrimaryButton
              type="submit"
              className="flex-1"
              disabled={changePasswordMutation.isPending || !!successMessage}
              isLoading={changePasswordMutation.isPending}
            >
              {t('changePassword.submit')}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}
